import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import Layout from "./Layout";
import BlogRightSidebar from "./BlogRightSidebar";
import { ArrowLeft, Clock, Tag, Menu, ArrowUpRight } from "lucide-react";
import { getPostsByTag } from "../data/blogPosts";

export default function TagPage() {
  const { isDark } = useTheme();
  const { tag } = useParams();
  const navigate = useNavigate();

  // Scroll to top when page loads or tag changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tag]);

  // Filter posts by tag (case-insensitive)
  const filteredPosts = getPostsByTag(tag);

  return (
    <Layout rightSidebar={<BlogRightSidebar />} showFloatingMenu={true}>
      {({ toggleMobileMenu }) => (
        <div className="flex flex-col items-center px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-12">
          <div className="w-full max-w-content">
            {/* Mobile Menu Button */}
            <div className="mb-8 lg:hidden">
              <button
                onClick={toggleMobileMenu}
                className={`p-2 rounded-lg ${
                  isDark
                    ? "text-gray-400 hover:text-white hover:bg-gray-700"
                    : "text-text-muted hover:text-text hover:bg-gray-100"
                }`}
                aria-label="Toggle menu"
              >
                <Menu size={20} />
              </button>
            </div>

            {/* Back Button */}
            <Link
              to="/"
              className={`inline-flex items-center gap-2 mb-8 transition-colors duration-base ${
                isDark
                  ? "text-gray-400 hover:text-primary-dark"
                  : "text-text-muted hover:text-primary"
              }`}
            >
              <ArrowLeft size={20} />
              <span className="text-sm">Back to all posts</span>
            </Link>

            {/* Page Header */}
            <header className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Tag
                  size={20}
                  className={isDark ? "text-primary-dark" : "text-primary"}
                />
                <h1
                  className={`text-4xl font-display font-semibold tracking-tight capitalize ${
                    isDark ? "text-white" : "text-text"
                  }`}
                >
                  {tag}
                </h1>
              </div>
              <p
                className={`text-base leading-normal ${
                  isDark ? "text-gray-400" : "text-text-muted"
                }`}
              >
                {filteredPosts.length} post
                {filteredPosts.length !== 1 ? "s" : ""} tagged with "{tag}"
              </p>
            </header>

            {/* Blog Posts */}
            {filteredPosts.length > 0 ? (
              <div className="space-y-10">
                {filteredPosts.map((post, index) => (
                  <article
                    key={index}
                    className={`group pb-10 border-b last:border-b-0 ${
                      isDark ? "border-border-dark" : "border-border"
                    }`}
                  >
                    {/* Tags at top - max 3 */}
                    <div className="flex gap-2 mb-1 -mt-2">
                      {post.tags.slice(0, 3).map((tag, tagIndex) => (
                        <Link
                          key={tagIndex}
                          to={`/tags/${tag.toLowerCase()}`}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border transition-all duration-base ${
                            isDark
                              ? "bg-background-dark text-gray-400 border-border-dark hover:border-primary-dark/50 hover:bg-border-dark"
                              : "bg-background-sidebar text-text-muted border-border/50 hover:border-primary/50 hover:bg-secondary/20"
                          }`}
                        >
                          <div
                            className={`w-1.5 h-1.5 rounded-full ${isDark ? "bg-primary-dark" : "bg-primary"}`}
                          ></div>
                          {tag}
                        </Link>
                      ))}
                    </div>

                    <h2
                      className={`text-2xl font-display font-medium leading-snug tracking-tight mb-3 -mt-1 ${
                        isDark ? "text-white" : "text-text"
                      }`}
                    >
                      <Link
                        to={`/blog/${post.slug}`}
                        className={`transition-colors duration-base inline-flex items-center gap-2 group ${isDark ? "hover:text-primary-dark" : "hover:text-primary"}`}
                      >
                        {post.title}
                        <ArrowUpRight
                          size={20}
                          className={`opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-base ${isDark ? "text-primary-dark" : ""}`}
                        />
                      </Link>
                    </h2>

                    <p
                      className={`text-base leading-normal mb-5 ${
                        isDark ? "text-gray-400" : "text-text-muted"
                      }`}
                    >
                      {post.excerpt}
                    </p>

                    {/* Date and Read Time - Bottom right */}
                    <div className="flex justify-end">
                      <div
                        className={`flex items-center gap-4 text-sm ${
                          isDark ? "text-gray-400" : "text-text-muted"
                        }`}
                      >
                        <div className="flex items-center gap-1.5">
                          <div
                            className={`w-1 h-1 rounded-full ${
                              isDark ? "bg-gray-400" : "bg-text-muted"
                            }`}
                          ></div>
                          <time dateTime={post.date}>
                            {new Date(post.date).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </time>
                        </div>
                        <span>•</span>
                        <div className="flex items-center gap-1.5">
                          <Clock size={14} />
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p
                  className={`text-lg mb-6 ${
                    isDark ? "text-gray-400" : "text-text-muted"
                  }`}
                >
                  No posts found with tag "{tag}"
                </p>
                <Link
                  to="/"
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg border transition-all duration-base ${
                    isDark
                      ? "text-gray-400 border-border-dark hover:text-white hover:border-primary-dark/50 hover:bg-border-dark"
                      : "text-text-muted border-border/50 hover:text-text hover:border-primary/50 hover:bg-secondary/20"
                  }`}
                >
                  <ArrowLeft size={16} />
                  Browse all posts
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
